import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

function CalculatorPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Solar Savings Calculator",
    "description": "Estimate your monthly and yearly savings with our free solar calculator.",
    "potentialAction": {
      "@type": "CalculateAction",
      "target": "https://www.yoursite.com/solar-savings-calculator?energy={energyConsumed}",
      "query-input": "required name=energyConsumed"
    }
  };
  const [formData, setFormData] = useState({
    monthlyBill: "",
    location: "Select Existing City",
    costPerUnit: "",
    systemSize: "",
  });

  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setResult(null);

    // Parse input values
    const monthlyBill = parseFloat(formData.monthlyBill);
    const costPerUnit = parseFloat(formData.costPerUnit);
    const systemSize = parseFloat(formData.systemSize);

    if (
      isNaN(monthlyBill) ||
      isNaN(costPerUnit) ||
      isNaN(systemSize) ||
      monthlyBill <= 0 ||
      costPerUnit <= 0 ||
      systemSize <= 0
    ) {
      setError("Please provide valid positive numbers for all fields.");
      return;
    }

    // Calculate monthly consumption in units
    const monthlyUnits = monthlyBill / costPerUnit;
    // Assume production factor = 150 units per KW per month
    const solarProduction = systemSize * 150;
    // Savings: assume you offset the lower of your consumption and solar production
    const monthlySaving = Math.min(monthlyUnits, solarProduction) * costPerUnit;
    const annualSaving = monthlySaving * 12;

    setResult({
      monthlyUnits: monthlyUnits.toFixed(2),
      solarProduction: solarProduction.toFixed(2),
      monthlySaving: monthlySaving.toFixed(2),
      annualSaving: annualSaving.toFixed(2),
    });
  };

  return (
    
    <div className="w-full min-h-screen b text-gray-800">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      
      <Navbar />

      {/* Top Section */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16 pt-30 text-center md:pt-30">
        <button className="mb-6 inline-block bg-gradient-to-r from-[#039147] to-[#FFD100] text-white font-medium px-6 py-3 rounded-full shadow-md hover:shadow-lg transition text-sm md:text-sm">
          + Calculate your savings with our Savings Calculator
        </button>
        <h1 className="text-2xl md:text-4xl font-medium text-gray-800 leading-tight mb-8">
          Discover How Much You Can Save by{" "}
        </h1>

        <span className="text-6xl md:text-8xl font-bold  text-[#039147] ">
          Going <span className="text-[#FFD100]">Solar !</span>
        </span>

        <div className="md:flex flex-row justify-center items-center md:space-x-7 mt-10 text-gray-600 md:text-lg">
          <div className="flex items-center space-x-2 bg-blue-100 px-4 py-1 rounded-full justify-center">
            <span>📈</span>
            <span className="text-blue-900">
              Data reviewed by solar experts.
            </span>
          </div>
          <div className="flex items-center space-x-2 md:mt-0 mt-2  bg-yellow-100 px-4 py-1 rounded-full justify-center">
            <span>🏠</span>
            <span className="text-yellow-900">
              100% honest from our calculations.
            </span>
          </div>
        </div>
      </section>

      {/* Calculator Form Section */}
      <section className="max-w-4xl mx-auto px-6 md:px-0 mb-16">
        <div className="md:bg-white rounded-xl md:shadow-md p-8 md:p-12">
          <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
            Current Yearly Electricity Consumption
          </h2>

          <form className="space-y-8" onSubmit={handleSubmit}>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Monthly Bill */}
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-600 mb-2 ml-1">
                  Monthly Electricity Bill
                  <span className="text-blue-600">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="monthlyBill"
                    value={formData.monthlyBill}
                    onChange={handleChange}
                    placeholder="3000"
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    required
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-4">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <span className="absolute right-4 top-1/2 translate-y-[10px] text-gray-400">
                    ₹
                  </span>
                </div>
              </div>

              {/* Location */}
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-600 mb-2 ml-1">
                  Location
                  <span className="text-blue-600">*</span>
                </label>
                <div className="relative">
                  <select
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                  >
                    <option className="text-gray-400">Select City</option>
                    <option>Mumbai</option>
                    <option>Delhi</option>
                    <option>Bangalore</option>
                    <option>Kolkata</option>
                    <option>Others</option>
                  </select>
                  <div className="absolute inset-y-0 left-0 flex items-center pl-4">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Cost Per Unit */}
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-600 mb-2 ml-1">
                  Electricity Cost/Unit
                  <span className="text-blue-600">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    name="costPerUnit"
                    value={formData.costPerUnit}
                    onChange={handleChange}
                    placeholder="6.50"
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    required
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-4">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                  </div>
                  <span className="absolute right-4 top-1/2 translate-y-[10px] text-gray-400">
                    ₹/unit
                  </span>
                </div>
              </div>

              {/* System Size */}
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-600 mb-2 ml-1">
                  Solar System Capacity
                  <span className="text-blue-600">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.1"
                    name="systemSize"
                    value={formData.systemSize}
                    onChange={handleChange}
                    placeholder="5.0"
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    required
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-4">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707"
                      />
                    </svg>
                  </div>
                  <span className="absolute right-4 top-1/2 translate-y-[10px] text-gray-400">
                    KW
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
              <button
                type="submit"
                className="flex-1 sm:flex-none bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
              >
                Calculate Savings
                <svg
                  className="w-4 h-4 ml-2 inline-block"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </button>

              {result && (
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      monthlyBill: "",
                      location: "Select Existing City",
                      costPerUnit: "",
                      systemSize: "",
                    });
                    setResult(null);
                  }}
                  className="flex-1 sm:flex-none bg-gray-100 text-gray-600 font-semibold px-8 py-4 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Reset Form
                </button>
              )}
            </div>

            {error && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-600">
                <svg
                  className="w-5 h-5 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <span>{error}</span>
              </div>
            )}
          </form>

          {result && (
            <div className="mt-8 p-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-sm">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                Your Solar Savings Analysis 🌞
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Consumption Card */}
                <div className="bg-white p-5 rounded-xl border border-blue-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <svg
                        className="w-6 h-6 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-500">
                      Monthly Consumption
                    </span>
                  </div>
                  <p className="text-3xl font-semibold text-gray-800">
                    {result.monthlyUnits}{" "}
                    <span className="text-lg text-gray-500">units</span>
                  </p>
                </div>

                {/* Production Card */}
                <div className="bg-white p-5 rounded-xl border border-blue-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <svg
                        className="w-6 h-6 text-orange-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707"
                        />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-500">
                      Solar Production
                    </span>
                  </div>
                  <p className="text-3xl font-semibold text-gray-800">
                    {result.solarProduction}{" "}
                    <span className="text-lg text-gray-500">units/month</span>
                  </p>
                </div>

                {/* Monthly Savings Card */}
                <div className="bg-white p-5 rounded-xl border border-blue-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <svg
                        className="w-6 h-6 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-500">
                      Monthly Savings
                    </span>
                  </div>
                  <p className="text-3xl font-semibold text-gray-800">
                    ₹{result.monthlySaving}
                  </p>
                </div>

                {/* Annual Savings Card */}
                <div className="bg-white p-5 rounded-xl border border-blue-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <svg
                        className="w-6 h-6 text-purple-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                        />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-500">
                      Annual Savings
                    </span>
                  </div>
                  <p className="text-3xl font-semibold text-gray-800">
                    ₹{result.annualSaving}
                  </p>
                </div>
              </div>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500 italic">
                  Based on average production data and current electricity rates
                </p>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default CalculatorPage;
